import React, { useState } from "react";
// @ts-ignore
import { useBeforeunload } from "react-beforeunload";
import { useTranslation } from "react-i18next";
import _ from "lodash";
import { useMutation } from "@apollo/react-hooks";
import {
  AppBar,
  Box,
  Collapse,
  Dialog,
  LinearProgress,
  Typography,
} from "@mui/material";
import {
  massRoleErrors,
  massRoleErrorsIsMobile,
  removeMassRoleErrors,
  removeMassRoleErrorsIsMobile,
} from "my-hr/configs/my-hr-table-defs";
import { OcDataTable, OcMassRoleToolbar, OcTransition } from "oc/components";
import { initialTableData } from "oc/configs/initial-table-data";
import { OC_UPDATE_PROPERTY } from "oc/graphql";
import { useOcTableState, useOcProperty } from "oc/hooks";

type MyHrMassRoleDialogProps = {
  showDialog: boolean;
  onShowDialog: () => void;
  onFinish: () => void;
  onCloseDialog: () => void;
  result: {
    errors?: any[];
    processed?: number;
    added?: number;
  } | null;
  infoText: React.ReactNode;
  successText: string;
  successInfo: string;
  toolbarLabel: string;
  errorText: string;
  action: "add" | "remove";
  isMobile: boolean;
};

export const MyHrMassRoleDialog = ({
  showDialog,
  onShowDialog,
  onFinish,
  onCloseDialog,
  result,
  infoText,
  successText,
  successInfo,
  toolbarLabel,
  errorText,
  action,
  isMobile,
}: MyHrMassRoleDialogProps) => {
  const { t } = useTranslation();

  const [selectedRowId, setSelectedRowId] = useState<string | null>(null);

  const tableId = "massRoleErrorsListTable";
  const [tableData, setTableData] = useOcTableState(tableId, {
    ...initialTableData,
    sortField: "employeeName",
    pageSize: 5,
  });

  const list = result?.errors || [];
  const sortedList = _.sortBy(list, initialTableData.sortField);
  const searchText = tableData?.searchText;
  const columName = tableData?.searchField;
  const filteredResult = list.filter(
    (item) => searchText && new RegExp(searchText, "i").test(item?.[columName]),
  );

  let resultTableData = {
    rows: tableData?.searchText?.length === 0 ? sortedList : filteredResult,
    total: result?.errors?.length || 0,
    page: tableData?.page,
    pageSize: tableData?.pageSize,
    searchField: tableData?.searchField,
    searchText: tableData?.searchText,
  };

  useBeforeunload((event: BeforeUnloadEvent) => {
    event.preventDefault();
    updateProperty({
      variables: {
        resource: "system",
        key: "importLocked",
        value: "false",
      },
    });
  });

  const { data: importProgress } = useOcProperty({
    resource: "system",
    key: "importProgress",
    pollInterval: 0,
    fetchPolicy: "network-only",
    subscription: true,
  });

  let progressInPercentString = importProgress ? `${importProgress}%` : "0%";
  if (result) {
    progressInPercentString = "100%";
  }

  const handleEnter = () => {
    updateProperty({
      variables: {
        resource: "system",
        key: "importProgress",
        value: "0",
      },
    });
    updateProperty({
      variables: {
        resource: "system",
        key: "importLocked",
        value: "true",
      },
    });
    updateProperty({
      variables: {
        resource: "system",
        key: "importCancelled",
        value: "false",
      },
    });
  };

  const handleFinish = () => {
    handleEnter();
    onFinish();
  };

  const [updateProperty] = useMutation(OC_UPDATE_PROPERTY, {
    onError(err) {
      console.log(err);
    },
  });

  const handleExit = () => {
    updateProperty({
      variables: {
        resource: "system",
        key: "importProgress",
        value: "0",
      },
    });
    updateProperty({
      variables: {
        resource: "system",
        key: "importLocked",
        value: "false",
      },
    });
  };

  const cancel = () => {
    handleEnter();
    handleExit();
    onCloseDialog();
  };

  let tableDef;

  if (action === "add") {
    tableDef = isMobile ? massRoleErrorsIsMobile : massRoleErrors;
  } else {
    tableDef = isMobile ? removeMassRoleErrorsIsMobile : removeMassRoleErrors;
  }

  const filteredTableDefMassRole = {
    id: massRoleErrors?.id,
    columns: massRoleErrors?.columns?.filter(
      (column) =>
        !massRoleErrorsIsMobile?.columns?.some(
          (mobileColumn) => mobileColumn?.id === column?.id,
        ),
    ),
  };

  const filteredTableDefMassRoleErrors = {
    id: removeMassRoleErrors?.id,
    columns: removeMassRoleErrors?.columns?.filter(
      (column) =>
        !removeMassRoleErrorsIsMobile?.columns?.some(
          (mobileColumn) => mobileColumn?.id === column?.id,
        ),
    ),
  };

  const onSelectRow = (o: { _id?: string }) => {
    if (isMobile) {
      const rowId = o && typeof o._id === "string" ? o._id : null;
      setSelectedRowId(selectedRowId === rowId ? null : rowId);
    }
  };

  return (
    <Dialog
      fullScreen
      open={showDialog}
      onClose={onShowDialog}
      TransitionComponent={OcTransition}
      TransitionProps={{
        onEntered: handleEnter,
        onExited: handleExit,
      }}
    >
      <AppBar
        position="relative"
        elevation={0}
        sx={{
          backgroundColor: "#E3E5E5",
        }}
      >
        <OcMassRoleToolbar
          handleCancel={cancel}
          handleFinish={handleFinish}
          dialogTitle={toolbarLabel}
          disabled={!result ? true : false}
        />
      </AppBar>
      <Box padding="16px">
        <Collapse in={true} timeout="auto" unmountOnExit>
          <Box
            display="flex"
            alignItems="center"
            marginTop="40px"
            marginBottom="56px"
          >
            <Box
              width={isMobile ? "100%" : "72%"}
              paddingLeft={isMobile ? "0px" : "14%"}
            >
              <LinearProgress
                variant="determinate"
                value={result ? 100 : Number.parseInt(String(importProgress ?? "0"), 10)}
                sx={{
                  backgroundColor: "#E3E5E5",
                  height: "16px",
                  borderRadius: "4px",
                }}
              />
            </Box>

            <Box
              display="flex"
              flexDirection="row"
              alignItems="center"
              justifyContent="center"
              paddingRight={isMobile ? "0px" : "14%"}
              paddingLeft="8px"
            >
              <Box>
                <Typography variant="body2" color="#757575">
                  {progressInPercentString}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Collapse>
        <Box style={{ marginTop: result ? "32px" : "0px" }}>{infoText}</Box>
        <Collapse in={result ? true : false} timeout="auto" unmountOnExit>
          <Box paddingTop="32px">
            <Box display="flex" flexDirection="column" alignItems="center">
              <Typography
                variant="h3"
                style={{
                  color:
                    (result?.errors?.length ?? 0) > 0 ? "#F3AA18" : "#2FA84F",
                }}
              >
                {(result?.errors?.length ?? 0) > 0 ? errorText : successText}
              </Typography>
            </Box>
            {isMobile && <Box height="16px" />}
            <Typography variant="body1">
              {result?.processed ?? 0} {t("MY_HR_ITEM_PROCESSED")}
            </Typography>
            <Typography variant="body1">
              {result?.added ?? 0} {successInfo}
            </Typography>
            <Box height="32px" />
            {(result?.errors?.length ?? 0) > 0 && (
              <OcDataTable
                data={resultTableData}
                loading={false}
                tableDef={tableDef}
                tableData={tableData}
                setTableData={setTableData}
                pagination={true}
                localPaging={true}
                onSelectRow={onSelectRow}
                filteredTableDef={
                  action === "add"
                    ? filteredTableDefMassRole
                    : filteredTableDefMassRoleErrors
                }
                selectedRowId={selectedRowId ?? undefined}
              />
            )}
          </Box>
        </Collapse>
      </Box>
    </Dialog>
  );
};

export default MyHrMassRoleDialog;
